import { useMemo, useState } from 'react';
import { IAllLandingBusiness } from '../../../../interfaces/ILandingBusiness';
import styles from './HomeFilters.module.css';

interface Filters {
  state: string;
  city: string;
  neighborhood: string;
  bedrooms: string;
  price: string;
  minArea: number;
  suites: string;
  garage: string;
  availability: string;
}

const EMPTY_FILTERS: Filters = {
  state: '',
  city: '',
  neighborhood: '',
  bedrooms: '',
  price: '',
  minArea: 0,
  suites: '',
  garage: '',
  availability: '',
};

const PRICE_RANGES = [
  { label: 'Até R$ 500.000', min: 0, max: 500000 },
  { label: 'R$ 500.000 - R$ 1.000.000', min: 500000, max: 1000000 },
  { label: 'R$ 1.000.000 - R$ 2.000.000', min: 1000000, max: 2000000 },
  { label: 'Acima de R$ 2.000.000', min: 2000000, max: Infinity },
];

const AVAILABILITY_OPTIONS = [
  { label: 'Na planta', value: 'blueprint' },
  { label: 'Lançamento', value: 'launched' },
  { label: 'Obras avançadas', value: 'advancedConstruction' },
  { label: 'Breve lançamento', value: 'upcomingLaunch' },
  { label: 'Em Obras', value: 'underConstruction' },
  { label: 'Pronto para morar', value: 'readyToMoveIn' },
];

const OUTRO_SENTINEL = '__outro__';

interface HomeFiltersProps {
  allLandingBusiness: IAllLandingBusiness[];
  onFilter: (filtered: IAllLandingBusiness[]) => void;
  primaryColor?: string;
  onFiltersChange?: (description: string) => void;
  showAllCities?: boolean;
}

export function HomeFilters({
  allLandingBusiness,
  onFilter,
  primaryColor,
  onFiltersChange,
  showAllCities,
}: HomeFiltersProps) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [panelOpen, setPanelOpen] = useState(false);
  const [pendingAdvanced, setPendingAdvanced] = useState({
    bedrooms: '',
    price: '',
    minArea: 0,
    suites: '',
    garage: '',
    availability: '',
  });

  // ── data derived from businesses ────────────────────────────────────────
  // LP (showAllCities=true) reads from lb.location sub-object
  // Home (showAllCities=false) reads from top-level fields

  const cities = useMemo(() => {
    const set = new Set<string>();
    allLandingBusiness.forEach((lb) => {
      if (lb.city) set.add(lb.city);
    });
    return Array.from(set).sort();
  }, [allLandingBusiness]);

  const neighborhoods = useMemo(() => {
    const set = new Set<string>();
    allLandingBusiness.forEach((lb) => {
      if (!filters.city || lb.city === filters.city) {
        if (lb.neighborhood) set.add(lb.neighborhood);
      }
    });
    return Array.from(set).sort();
  }, [allLandingBusiness, filters.city]);

  function collectRangeValues(
    field: 'bedrooms' | 'suites' | 'garage',
    plusThreshold?: number
  ): number[] {
    const set = new Set<number>();
    allLandingBusiness.forEach((lb) => {
      const value = lb[field];
      if (value === undefined) return;
      if (Array.isArray(value)) {
        const min = value[0];
        const max = value[value.length - 1];
        const effectiveMax =
          plusThreshold !== undefined ? Math.min(max, plusThreshold) : max;
        for (let i = min; i <= effectiveMax; i++) set.add(i);
      } else {
        const effectiveVal =
          plusThreshold !== undefined && value >= plusThreshold
            ? plusThreshold
            : value;
        set.add(effectiveVal);
      }
    });
    return Array.from(set).sort((a, b) => a - b);
  }

  function matchesRange(
    value: number | number[] | undefined,
    filterN: number,
    plusThreshold?: number
  ): boolean {
    if (value === undefined) return false;
    if (Array.isArray(value)) {
      const min = value[0];
      const max = value[value.length - 1];
      if (plusThreshold !== undefined && filterN >= plusThreshold)
        return max >= plusThreshold;
      return filterN >= min && filterN <= max;
    } else {
      if (plusThreshold !== undefined && filterN >= plusThreshold)
        return value >= plusThreshold;
      return value === filterN;
    }
  }

  const bedroomOptions = useMemo(
    () => collectRangeValues('bedrooms', 4).filter((n) => n > 0),
    [allLandingBusiness]
  );

  const suiteOptions = useMemo(
    () => collectRangeValues('suites', 3),
    [allLandingBusiness]
  );

  const garageOptions = useMemo(
    () => collectRangeValues('garage', 3),
    [allLandingBusiness]
  );

  // LP: only show stages that exist in businesses; Home: show all
  const availableStages = useMemo(() => {
    if (!showAllCities) return AVAILABILITY_OPTIONS;
    const found = new Set<string>();
    allLandingBusiness.forEach((lb) => {
      if (lb.constructionStage) {
        Object.entries(lb.constructionStage).forEach(([key, val]) => {
          if (val) found.add(key);
        });
      }
    });
    return AVAILABILITY_OPTIONS.filter((o) => found.has(o.value));
  }, [allLandingBusiness, showAllCities]);

  // ── filter logic ────────────────────────────────────────────────────────

  function applyFilters(current: Filters) {
    const result = allLandingBusiness.filter((lb) => {
      if (current.city && lb.city !== current.city) return false;
      if (current.neighborhood && lb.neighborhood !== current.neighborhood)
        return false;

      if (current.bedrooms) {
        if (!matchesRange(lb.bedrooms, Number(current.bedrooms), 4))
          return false;
      }

      if (current.price) {
        const range = PRICE_RANGES.find((r) => r.label === current.price);
        if (range) {
          const price = lb.unitsMinimumValue ?? lb.unitsMaximumValue;
          if (price === undefined) return false;
          if (price < range.min || price > range.max) return false;
        }
      }

      if (current.minArea > 0) {
        const areaMin = lb.squareMeters?.[0];
        if (areaMin === undefined || areaMin < current.minArea) return false;
      }

      if (current.suites) {
        if (!matchesRange(lb.suites, Number(current.suites), 3)) return false;
      }

      if (current.garage) {
        if (!matchesRange(lb.garage, Number(current.garage), 3)) return false;
      }

      if (current.availability) {
        const stage = lb.constructionStage as
          | Record<string, boolean | undefined>
          | undefined;
        if (!stage?.[current.availability]) return false;
      }

      return true;
    });
    onFilter(result);
  }

  function isOutro(value: string): boolean {
    return value === OUTRO_SENTINEL;
  }

  function labelOf(value: string, label: string): string {
    return isOutro(value) ? `${label}: Outro` : `${label}: ${value}`;
  }

  function buildDescription(f: Filters): string {
    const parts: string[] = [];
    if (f.city)
      parts.push(isOutro(f.city) ? 'Cidade: Outro' : `Cidade: ${f.city}`);
    if (f.neighborhood)
      parts.push(
        isOutro(f.neighborhood) ? 'Bairro: Outro' : `Bairro: ${f.neighborhood}`
      );
    if (f.bedrooms) parts.push(labelOf(f.bedrooms, 'Quartos'));
    if (f.price) parts.push(labelOf(f.price, 'Preço'));
    if (f.minArea > 0) parts.push(`Área mínima: ${f.minArea}m²`);
    if (f.suites) parts.push(labelOf(f.suites, 'Suítes'));
    if (f.garage) parts.push(labelOf(f.garage, 'Vagas'));
    if (f.availability) parts.push(labelOf(f.availability, 'Disponibilidade'));
    return parts.join(' | ');
  }

  function hasOutroValue(...values: string[]): boolean {
    return values.some(isOutro);
  }

  function handleChange(field: keyof Filters, value: string | number) {
    const next = { ...filters, [field]: value };
    if (field === 'city') next.neighborhood = '';
    setFilters(next);

    if (isOutro(String(value))) {
      onFilter([]);
      onFiltersChange?.(buildDescription(next));
      return;
    }

    applyFilters(next);
    onFiltersChange?.(buildDescription(next));
  }

  function handleApply() {
    applyFilters(filters);
    onFiltersChange?.(buildDescription(filters));
  }

  function handleClear() {
    setFilters(EMPTY_FILTERS);
    setPendingAdvanced({
      bedrooms: '',
      price: '',
      minArea: 0,
      suites: '',
      garage: '',
      availability: '',
    });
    onFilter(allLandingBusiness);
    onFiltersChange?.('');
  }

  function handleApplyAdvanced() {
    const next = { ...filters, ...pendingAdvanced };
    setFilters(next);
    setPanelOpen(false);

    if (
      hasOutroValue(
        pendingAdvanced.bedrooms,
        pendingAdvanced.price,
        pendingAdvanced.suites,
        pendingAdvanced.garage,
        pendingAdvanced.availability
      )
    ) {
      onFilter([]);
      onFiltersChange?.(buildDescription(next));
      return;
    }

    applyFilters(next);
    onFiltersChange?.(buildDescription(next));
  }

  return (
    <>
      <div className={styles.filterBar}>
        {/* Cidade */}
        <select
          className={styles.filterSelect}
          value={filters.city}
          onChange={(e) => handleChange('city', e.target.value)}
          disabled={cities.length === 0}
        >
          <option value="">Cidade</option>
          {cities.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          {showAllCities && <option value={OUTRO_SENTINEL}>Outro</option>}
        </select>

        {/* Bairro */}
        <select
          className={styles.filterSelect}
          value={filters.neighborhood}
          onChange={(e) => handleChange('neighborhood', e.target.value)}
          disabled={!filters.city}
        >
          <option value="">Bairro</option>
          {neighborhoods.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
          {showAllCities && <option value={OUTRO_SENTINEL}>Outro</option>}
        </select>

        {/* Quartos — main bar para Home e LP */}
        <select
          className={styles.filterSelect}
          value={filters.bedrooms}
          onChange={(e) => handleChange('bedrooms', e.target.value)}
        >
          <option value="">Quartos</option>
          {bedroomOptions.map((n) => (
            <option key={n} value={n}>
              {n >= 4 ? `${n}+` : n} quarto{n !== 1 ? 's' : ''}
            </option>
          ))}
          {showAllCities && <option value={OUTRO_SENTINEL}>Outro</option>}
        </select>

        {/* Personalizar */}
        <button
          className={styles.filterBtn}
          onClick={() => {
            setPendingAdvanced({
              bedrooms: filters.bedrooms,
              price: filters.price,
              minArea: filters.minArea,
              suites: filters.suites,
              garage: filters.garage,
              availability: filters.availability,
            });
            setPanelOpen(true);
          }}
        >
          <i className="material-icons-outlined" style={{ fontSize: 16 }}>
            tune
          </i>
          Personalizar
        </button>

        {/* Limpar Filtros */}
        <button className={styles.filterBtnClear} onClick={handleClear}>
          <i className="material-icons-outlined" style={{ fontSize: 16 }}>
            close
          </i>
          Limpar Filtros
        </button>

        {/* Aplicar Filtros — sempre por último */}
        <button
          className={styles.filterBtn}
          style={
            primaryColor
              ? {
                  backgroundColor: primaryColor,
                  color: '#fff',
                  borderColor: primaryColor,
                }
              : undefined
          }
          onClick={handleApply}
        >
          <i className="material-icons-outlined" style={{ fontSize: 16 }}>
            search
          </i>
          Aplicar Filtros
        </button>
      </div>

      {panelOpen && (
        <>
          <div className={styles.overlay} onClick={() => setPanelOpen(false)} />
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <button
                className={styles.panelBackBtn}
                onClick={() => setPanelOpen(false)}
              >
                <i className="material-icons-outlined">arrow_back</i>
                <span>Voltar</span>
              </button>
              <p className={styles.panelTitle}>Filtros adicionais</p>
              <button
                className={styles.panelClose}
                onClick={() => setPanelOpen(false)}
              >
                <i className="material-icons-outlined">close</i>
              </button>
            </div>
            <p className={styles.panelSubtitle}>
              Ajuste os filtros para encontrar o imóvel perfeito para você.
            </p>

            <div className={styles.panelField}>
              <label className={styles.panelLabel}>Valor do Imóvel</label>
              <select
                className={styles.panelSelect}
                value={pendingAdvanced.price}
                onChange={(e) =>
                  setPendingAdvanced((p) => ({ ...p, price: e.target.value }))
                }
              >
                <option value="">Valor do Imóvel</option>
                {PRICE_RANGES.map((r) => (
                  <option key={r.label} value={r.label}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.panelField}>
              <label className={styles.panelLabel}>Área mínima</label>
              <div className={styles.sliderWrapper}>
                <input
                  type="range"
                  min={0}
                  max={500}
                  step={10}
                  value={pendingAdvanced.minArea}
                  className={styles.slider}
                  onChange={(e) =>
                    setPendingAdvanced((p) => ({
                      ...p,
                      minArea: Number(e.target.value),
                    }))
                  }
                />
                <div className={styles.sliderLabels}>
                  <span>
                    {pendingAdvanced.minArea === 0
                      ? 'A partir de Qualquer'
                      : `A partir de ${pendingAdvanced.minArea} m²`}
                  </span>
                  <span>500 m²</span>
                </div>
              </div>
            </div>

            <div className={styles.panelField}>
              <label className={styles.panelLabel}>Suítes</label>
              <select
                className={styles.panelSelect}
                value={pendingAdvanced.suites}
                onChange={(e) =>
                  setPendingAdvanced((p) => ({ ...p, suites: e.target.value }))
                }
              >
                <option value="">Suítes</option>
                {suiteOptions.map((n) => (
                  <option key={n} value={n}>
                    {n >= 3 ? `${n}+` : n} suíte{n !== 1 ? 's' : ''}
                  </option>
                ))}
                {showAllCities && <option value={OUTRO_SENTINEL}>Outro</option>}
              </select>
            </div>

            <div className={styles.panelField}>
              <label className={styles.panelLabel}>Vagas de garagem</label>
              <select
                className={styles.panelSelect}
                value={pendingAdvanced.garage}
                onChange={(e) =>
                  setPendingAdvanced((p) => ({ ...p, garage: e.target.value }))
                }
              >
                <option value="">Vagas de garagem</option>
                {garageOptions.map((n) => (
                  <option key={n} value={n}>
                    {n >= 3 ? `${n}+` : n} vaga{n !== 1 ? 's' : ''}
                  </option>
                ))}
                {showAllCities && <option value={OUTRO_SENTINEL}>Outro</option>}
              </select>
            </div>

            <div className={styles.panelField}>
              <label className={styles.panelLabel}>Disponibilidade</label>
              <select
                className={styles.panelSelect}
                value={pendingAdvanced.availability}
                onChange={(e) =>
                  setPendingAdvanced((p) => ({
                    ...p,
                    availability: e.target.value,
                  }))
                }
              >
                <option value="">Disponibilidade</option>
                {availableStages.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
                {showAllCities && <option value={OUTRO_SENTINEL}>Outro</option>}
              </select>
            </div>

            <button
              className={styles.panelApplyBtn}
              style={
                primaryColor
                  ? { backgroundColor: primaryColor, color: '#fff' }
                  : undefined
              }
              onClick={handleApplyAdvanced}
            >
              Aplicar filtros
            </button>
          </div>
        </>
      )}
    </>
  );
}
